import {
    Component,
    AfterViewInit,
    ViewChild,
    ElementRef,
    Input,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Output,
    EventEmitter
} from "@angular/core";
import { FitPlugin, Graph, PanningHandler } from "@maxgraph/core";
import { MaxGraphSetup } from "./setup/max-graph-setup";
import { CircuitLayoutBuilder } from "./layout/circuit-layout-builder.service";
import { DiagramDrawerService } from "./drawer/diagram-drawer.service";
import {
    CircuitDiagram,
    CircuitElementType,
    DiagramLegendGroup
} from "./models/diagram.models";
import { AnyDrawCommand } from "@nms-ng2/app/shared/services/layout-manager/drawing.models";
import { DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT, TOPOLOGY_SETTINGS } from "./models/diagram.constants";
import { DwdmCircuitDiagramLegendService } from "./legend/dwdm-circuit-diagram-legend.service";
import { PopoverVisibilityManagerService } from "./popover/popover-visibility-manager.service";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { MaxGraphEventBus } from "./setup/max-graph-event-bus";
import { SimpleLayoutElement } from "@nms-ng2/app/shared/services/layout-manager/layout-manager.models";
import { DwdmCircuitWizardUtilsService } from "../wizard/utils/dwdm-circuit-wizard-utils.service";

/**
 * Componente responsável por criar um diagrama
 * para representar um circuito DWDM usando o MaxGraph
 */
@Component({
    selector: "dwdm-diagram",
    templateUrl: "./diagram.component.html",
    styleUrls: ["./diagram.component.scss"]
})
export class DwdmDiagramComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
    @ViewChild("diagramContainer", { static: false })
    container!: ElementRef;

    @Input()
    showHeader: boolean = true;

    @Input()
    circuitDiagram: CircuitDiagram;

    @Input()
    rootElementParameters?: SimpleLayoutElement;

    @Input()
    canFitVertically: boolean = false;

    private readonly MAX_ZOOM: number = 3;
    private readonly RX_TX_LABEL_WIDTH: number = 50;
    private readonly SPACING: number = 135;

    private readonly maxGraphSetup: MaxGraphSetup;

    private legend: Map<CircuitElementType, DiagramLegendGroup>;
    private diagram: Graph;
    private panningHandler: PanningHandler;
    private fitPlugin: FitPlugin;

    private legendKeys: Array<CircuitElementType>;

    private readonly updateDiagramSubject = new Subject<void>();
    private readonly refreshDiagramDebounce$ = this.updateDiagramSubject.pipe(debounceTime(500));
    private readonly refreshDiagramSubscription: Subscription;


    constructor(private readonly layoutBuilder: CircuitLayoutBuilder, private readonly diagramDrawer: DiagramDrawerService,
            private readonly dwdmCircuitDiagramLegendService: DwdmCircuitDiagramLegendService,
            private readonly popoverVisibilityManagerService: PopoverVisibilityManagerService,
            private readonly maxGraphEventBus: MaxGraphEventBus,
            private readonly dwdmCircuitWizardUtilsService: DwdmCircuitWizardUtilsService) {
        this.maxGraphSetup = new MaxGraphSetup({});

        this.refreshDiagramSubscription = this.refreshDiagramDebounce$.subscribe(() => {
            this.refresh();
        });
    }

    ngOnInit(): void {
        this.legend = this.dwdmCircuitDiagramLegendService.retrieveDiagramLegend();
        this.legendKeys = this.legend ? Array.from(this.legend.keys()) : [];
    }

    ngAfterViewInit(): void {
        this.diagram = this.maxGraphSetup.setup(this.container, this.maxGraphEventBus);
        this.panningHandler = this.diagram.getPlugin<PanningHandler>("PanningHandler");
        this.fitPlugin = this.diagram.getPlugin<FitPlugin>("fit");

        setTimeout(() => {
            this.drawCircuitDiagram();
        }, 100);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["circuitDiagram"] && !changes["circuitDiagram"].firstChange) {
            this.updateDiagramSubject.next();
        }
    }

    ngOnDestroy(): void {
        this.popoverVisibilityManagerService.destroy();
        this.refreshDiagramSubscription?.unsubscribe();
        this.maxGraphSetup.destroy();
    }

    /**
     * Realiza o desenho do diagrama de circuito.
     *
     * A criação do layout do circuito é delegada para o CircuitLayoutBuilder.
     * O cálculo de posicionamento dos elementos é feito internamente pelo LayoutManager.
     * O processo de renderizar os elementos é delegado para o DiagramDrawer.
     */
    drawCircuitDiagram() {
        const rootParameters = this.getRootParameters();
        const commands: AnyDrawCommand[] = this.layoutBuilder.createCircuitCommands(this.circuitDiagram, rootParameters);

        this.diagramDrawer.draw(this.diagram, commands);
        setTimeout(() => this.dwdmCircuitWizardUtilsService.hideDiagram.emit(false));
        this.zoomReset();
    }

    /**
     * Gera as propriedades de layout do elemento raiz do diagrama de circuito.
     *
     * Faz isso unindo e sobrescrevendo em ordem:
     *     1. Configurações padrão (`DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT`)
     *     2. Parâmetros fornecidos pelo usuário (`this.rootElementParameters`)
     *     3. A largura calculada dinamicamente (via `getDiagramWidth()`)
     *
     * Ou seja, a largura calculada prevalece sobre qualquer valor de `width` definido
     * previamente em `rootElementParameters` ou `DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT`.
     *
     * @returns {Object} Objeto com as propriedades de layout do elemento raiz do diagrama de circuito.
     *
     * @example
     * // DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT = { type: "root", spacing: 10, height: 200 }
     * // this.rootElementParameters = { height: 300, width: 500 }
     * // getDiagramWidth() = 1000
     * // Resultado: { type: "root", spacing: 10, height: 300, width: 1000 }
     */
    private getRootParameters(): SimpleLayoutElement {
        const width = this.getDiagramWidth();

        return Object.assign({ }, DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT, this.rootElementParameters, { width });
    }

    /**
     * Atualiza o diagrama de circuito.
     *
     * Limpa o diagrama atual e constrói um novo diagrama.
     */
    refresh() {
        this.diagramDrawer.clear(this.diagram);
        this.drawCircuitDiagram();
    }

    /**
     * Ajusta o tamanho do diagrama para caber por completo na largura disponível da tela.
     * Esta função é útil nos casos onde o diagrama tem uma largura muito maior (e.g. muitos equipamentos intermediários).
     */
    fitHorizontal() {
        this.fitPlugin.fit({ margin: 0, ignoreHeight: true });
        this.diagram.center(false, true);
        this.setPanning(false);
        this.refreshAnimations();
    }

    /**
     * Ajusta o tamanho do diagrama para caber por completo na largura disponível da tela.
     * Esta função é útil nos casos onde o diagrama tem uma largura muito maior (e.g. muitos equipamentos intermediários).
     */
    fitVertical() {
        if (this.canFitVertically) {
            const scrollbarOffset = 9;
            const maxHeight = this.container.nativeElement.offsetHeight - scrollbarOffset;

            this.fitPlugin.fit({ margin: 0, ignoreWidth: true, maxHeight });
            this.setPanning(this.canPan());
            this.refreshAnimations();
        }
    }

    /**
     * Redefine os valores de zoom para o padrão inicial e altera a posição do scroll para voltar ao início na esquerda.
     * Vale lembrar que diagramas com muitos intermediários podem ter um estado inicial que já precisa de scroll horizontal e pan.
     */
    zoomReset() {
        this.diagram.zoomActual();
        this.diagram.center(false, true);
        this.diagram.container.scrollLeft = 0;
        this.setPanning(this.canPan());
        this.refreshAnimations();
    }

    /**
     * Aumenta o zoom do diagrama se possível.
     * Função limitada ao valor máximo de zoom (@see this.MAX_ZOOM).
     */
    zoomIn() {
        if (this.canZoomIn()) {
            this.diagram.zoomIn();
            this.setPanning(this.canPan());
            this.refreshAnimations();
        }
    }

    /**
     * Diminui o zoom do diagrama se possível.
     * Função limitada a existência de scroll horizontal, ou seja,
     * enquanto houver scroll horizontal, é possível diminuir o zoom.
     */
    zoomOut() {
        if (this.canZoomOut()) {
            this.diagram.zoomOut();
            this.setPanning(this.canPan());
            this.refreshAnimations();
        }
    }

    /**
     * Verifica se pode usar da funcionalidade de panning para mover o diagram.
     * É possível caso houver scroll horizontal.
     */
    canPan(): boolean {
        return this.hasHorizontalScroll();
    }

    /**
     * Verifica se pode aumentar o zoom do diagram.
     * É possível caso o zoom atual seja menor que o valor máximo de zoom (@see this.MAX_ZOOM).
     */
    canZoomIn(): boolean {
        return this.diagram?.view.scale < this.MAX_ZOOM;
    }

    /**
     * Verifica se pode diminuir o zoom do diagram.
     * É possível caso houver scroll horizontal.
     */
    canZoomOut(): boolean {
        return this.hasHorizontalScroll();
    }

    /**
     * Verifica existe scroll horizontal no diagrama.
     */
    private hasHorizontalScroll() {
        return this.diagram?.container.scrollWidth > this.diagram?.container.clientWidth;
    }

    /**
     * Define se a funcionalidade de panning (i.e. mover o diagrama com o mouse) está ativa.
     *
     * @param isEnabled valor booleano a ser definido para a funcionalidade de panning.
     */
    private setPanning(isEnabled: boolean) {
        this.diagram.setPanning(isEnabled);
        (this.panningHandler as any).ignoreCell = isEnabled;
    }

    /**
     * Atualiza as animações do diagrama.
     */
    private refreshAnimations() {
        this.diagramDrawer.handleAnimations(this.diagram);
    }

    /**
     * Calcula uma estimativa para o tamanho mínimo que o diagrama deveria ter.
     * Caso o valor mínimo seja menor do que o disponível na tela, usa o valor disponível, caso contrário, usa o valor calculado.
     *
     * O MaxGraph insere alguns pixels extras para sombra, borda e talvez outras coisas.
     * A configuração de sombra foi removida, mas ainda assim existe 1 pixel extra. Além disso,
     * o FitPlugin faz o cálculo de uma forma diferente, de modo que o reset do Zoom e o FitHorizontal
     * tenham comportamentos diferentes por uma questão de poucos pixels de diferença. Então para
     * não iniciar o diagrama já com o scroll ativado, e para que o Zoom e o FitHorizontal se comportem de
     * forma idêntica (em diagramas com largura padrão), é necessário fazer `clientWidth - 3`.
     */
    private getDiagramWidth() {
        const endSize = TOPOLOGY_SETTINGS.DEVICE.END.width + this.RX_TX_LABEL_WIDTH;
        const intermediateSize = TOPOLOGY_SETTINGS.DEVICE.INTERMEDIATE_FULL.width + (this.RX_TX_LABEL_WIDTH * 2) + this.SPACING;
        const intermediatesCount = this.circuitDiagram?.intermediates?.length || 0;

        const minWidth = (endSize * 2) + this.SPACING + (intermediateSize * intermediatesCount);
        const elementWidth = this.container.nativeElement.clientWidth;

        return (minWidth < elementWidth) ? elementWidth - 3 : minWidth;
    }
}
